import { maintenanceTaskSteps, maintenanceTasks } from '@markku/db'
import { createInsertSchema, createSelectSchema } from 'drizzle-zod'
import { z } from 'zod'
import { withAttachments } from './attachment'

// Base schemas
export const selectMaintenanceTaskSchema = createSelectSchema(maintenanceTasks)
export const insertMaintenanceTaskSchema = createInsertSchema(maintenanceTasks)
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
    deletedAt: true,
    createdByUserId: true, // Auto-set via contextFields
  })
  .extend({
    propertyId: z.number().nullable().optional(), // Allow null for system tasks
  })
export const updateMaintenanceTaskSchema = insertMaintenanceTaskSchema.partial()

// Task step schemas
export const selectMaintenanceTaskStepSchema =
  createSelectSchema(maintenanceTaskSteps)
export const insertMaintenanceTaskStepSchema = createInsertSchema(
  maintenanceTaskSteps,
).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
})
export const updateMaintenanceTaskStepSchema =
  insertMaintenanceTaskStepSchema.partial()

// Metadata schema specific to maintenance task attachments
export const maintenanceTaskAttachmentMetadataSchema = z
  .object({
    type: z
      .enum([
        'instruction_video',
        'reference_image',
        'completion_photo',
        'before_photo',
        'after_photo',
        'document',
        'other',
      ])
      .optional(),
    order: z.number().optional(),
    duration: z.number().optional(), // Duration in seconds for videos
    stepId: z.number().optional(), // If attachment is for a specific step
  })
  .optional()

// Schema with relations
export const selectMaintenanceTaskWithStepsSchema = withAttachments(
  selectMaintenanceTaskSchema.extend({
    steps: z.array(selectMaintenanceTaskStepSchema),
  }),
)

// Extended step schema with attachments
export const selectMaintenanceTaskStepWithAttachmentsSchema = withAttachments(
  selectMaintenanceTaskStepSchema,
)

// Create task with steps schema
export const createMaintenanceTaskWithStepsSchema = z.object({
  name: z.string().min(1),
  description: z.string().optional(),
  category: z.string().optional(),
  deadline: z.string().optional(),
  schedulingType: z
    .enum([
      'periodic',
      'annual',
      'seasonal_spring',
      'seasonal_fall',
      'seasonal_winter',
      'seasonal_summer',
    ])
    .optional(),
  estimatedCost: z.string().nullable().optional(),
  defaultFrequencyDays: z.number().optional(),
  estimatedDurationMinutes: z.number().optional(),
  propertyId: z.number().nullable().optional(), // NULL = system task, NOT NULL = property-specific
  steps: z
    .array(
      z.object({
        stepNumber: z.number(),
        name: z.string().min(1),
        description: z.string().optional(),
        instruction: z.string().optional(),
      }),
    )
    .optional(),
})

// Type exports
export type MaintenanceTask = z.infer<typeof selectMaintenanceTaskSchema>
export type MaintenanceTaskInsert = z.infer<typeof insertMaintenanceTaskSchema>
export type MaintenanceTaskUpdate = z.infer<typeof updateMaintenanceTaskSchema>
export type MaintenanceTaskWithSteps = z.infer<
  typeof selectMaintenanceTaskWithStepsSchema
>
export type MaintenanceTaskStepWithAttachments = z.infer<
  typeof selectMaintenanceTaskStepWithAttachmentsSchema
>
export type MaintenanceTaskAttachmentMetadata = z.infer<
  typeof maintenanceTaskAttachmentMetadataSchema
>

export type MaintenanceTaskStep = z.infer<
  typeof selectMaintenanceTaskStepSchema
>
export type MaintenanceTaskStepInsert = z.infer<
  typeof insertMaintenanceTaskStepSchema
>
export type MaintenanceTaskStepUpdate = z.infer<
  typeof updateMaintenanceTaskStepSchema
>

export type CreateMaintenanceTaskWithSteps = z.infer<
  typeof createMaintenanceTaskWithStepsSchema
>
